// Wraps useMealPlannerData in a Context so every routed page (Recipes, Plan,
// Grocery, Defaults) can reach the same shared state without prop-drilling
// through a router. This replaces the old approach of the top-level
// <MealPlanner> component holding state and passing it down to whichever
// tab was active.
import { createContext, useContext, type ReactNode } from "react";
import { useMealPlannerData } from "./useMealPlannerData";
import { COLORS } from "../lib/constants";
import type { Day, DishType, MealSlot, PlanDefaults, PlansByWeek, Recipe, RecipeById, WeekPlan } from "../lib/types";
import type { MealRef } from "../lib/planScaling";

// What TypeScript code reads from the context. useMealPlannerData is still
// JavaScript, so this is declared by hand; the .jsx pages aren't checked
// against it. Add a field here when a .ts/.tsx file starts using it, and
// type the rest when the hook itself moves to TypeScript (ADR-0017).
export interface MealPlannerState {
  loaded: boolean;
  householdId: string;
  userId: string;
  recipes: Recipe[];
  recipeById: RecipeById;
  plansByWeek: PlansByWeek;
  planDefaults: PlanDefaults;
  weekStart: string;
  setWeekStart: (weekStart: string) => void;
  weekData: WeekPlan;
  canEditRecipeContent: (recipe: Recipe) => boolean;
  isFavorite: (recipe: Recipe) => boolean;
  mySharedRecipeIds: Set<string>;
  // Recipes shared directly with me -> when (ISO), newest share.
  sharedWithMeIds: Map<string, string>;
  recipePlanDates: (recipeId: string) => unknown[];
  applyWeekAsTemplate: (sourceWeekKey: string) => unknown;
  assignRecipeToPlan: (...args: unknown[]) => unknown;
  removeRecipeFromPlan: (...args: unknown[]) => unknown;
  scaleRecipe: (recipe: Recipe, factor: number) => Recipe;
  toggleFavorite: (recipeId: string) => unknown;
  saveRecipe: (recipe: Recipe) => Promise<string>; // resolves to the recipe's id
  // Adding recipes (the Add sheet and the writing steps, slices 7a and 7b).
  importRecipes: (recipes: Recipe[]) => Promise<Recipe[] | undefined>;
  importRecipeFromUrl: (url: string) => Promise<{ recipe: unknown; dedupRecipeId: string | null; normalizedUrl: string }>;
  registerRecipeUrlImport: (normalizedUrl: string, recipeId: string) => void;
  claimRecipeUrlImport: (recipeId: string) => Promise<void>;
  // The Plan screens (slice 5b). weekData is the week being viewed.
  updateCurrentWeek: (updater: (week: WeekPlan) => WeekPlan) => void;
  setDish: (day: Day, slot: MealSlot, dishType: DishType, recipeId: string) => void;
  removeDishRow: (day: Day, slot: MealSlot, dishType: DishType) => void;
  placeLeftovers: (day: Day, slot: MealSlot, dishType: DishType, what: { source?: MealRef | null; recipeId?: string | null }) => void;
  // Moves a whole meal within this week, swapping with what is there (slice 5d).
  moveMeal: (from: { day: Day; slot: MealSlot }, to: { day: Day; slot: MealSlot }) => void;
  // The recipe share sheet (slice 8a).
  setRecipePublic: (recipeId: string, isPublic: boolean) => Promise<void>;
  noteEditableByOthers: (recipeId: string, value: boolean) => void;
  // Marks a planned dish cooked or not, in any loaded week (slice 7c).
  setCooked: (weekStart: string, day: Day, slot: MealSlot, dishType: DishType, cooked: boolean) => void;
  // Re-reads the plan after someone else changed it (slice 6a).
  reloadWeekPlans: () => Promise<void>;
  setDayBudget: (day: Day, minutes: number | null) => void;
  clearDay: (day: Day) => void;
  addWeekItem: (listType: "snack" | "preserves" | "medicinal", recipeId: string) => Promise<void>;
  removeWeekItem: (listType: "snack" | "preserves" | "medicinal", index: number) => Promise<void>;
  clearCurrentWeek: () => void;
  weeklyTotalTime: number;
  totalGroceryItems: number;
  // Deletes a recipe the person owns, or removes someone else's from their
  // library (leave_or_delete_recipe decides which).
  deleteRecipe: (recipeId: string) => void;
  colors: typeof COLORS;
  [field: string]: unknown;
}

// Exported so a page can be rendered with sample data outside the app
// (previews and checks); the app itself goes through the provider below.
export const MealPlannerContext = createContext<MealPlannerState | null>(null);

export function MealPlannerProvider({ householdId, userId, children }: { householdId: string; userId: string; children: ReactNode }) {
  const data = useMealPlannerData(householdId, userId);
  const value = { ...data, colors: COLORS } as unknown as MealPlannerState;
  return <MealPlannerContext.Provider value={value}>{children}</MealPlannerContext.Provider>;
}

export function useMealPlanner(): MealPlannerState {
  const ctx = useContext(MealPlannerContext);
  if (!ctx) throw new Error("useMealPlanner must be used within a MealPlannerProvider");
  return ctx;
}
